/**
 * CONFIGURACIÓN GLOBAL DE LA APLICACIÓN AR
 * Centraliza todas las constantes y configuraciones con tipos TypeScript
 */

import type {
  UIConfig,
  HitboxConfig,
  AppState,
  ARConfig,
  CameraConfig,
  ClickableArea
} from '@types';

// CONFIGURACIÓN DE UI
export const UI_CONFIG: UIConfig = {
  INITIALIZATION_DELAY: 1000,
  CALIBRATION_UPDATE_INTERVAL: 100
} as const;

// CONFIGURACIÓN DE HITBOXES
export const HITBOX_CONFIG: HitboxConfig = {
  // Propiedades requeridas
  PADDING_FACTOR: 1.2,
  MIN_BUTTON_SIZE: 50,
  MAX_BUTTON_SIZE: 200,
  SCALE_FACTOR: 1.0,
  DISTANCE_THRESHOLD: 10,
  UPDATE_INTERVAL: 100,
  
  // Configuración ajustada: padding reducido para mayor precisión
  PADDING_MULTIPLIER_WIDTH: 3,
  PADDING_MULTIPLIER_HEIGHT: 3,
  
  // Configuración de visualización
  OVERLAY_STYLE: {
    fill: 'rgba(255, 0, 0, 0.1)',
    stroke: 'red',
    strokeWidth: 2
  },
  
  // Configuración de escala adaptativa
  SCALE_LIMITS: {
    min: 0.2,
    max: 1.0,
    baseFactor: 3
  },
  
  // Factor de ajuste de proyección
  PROJECTION_SCALE: 0.25
} as const;

// ESTADOS DE LA APLICACIÓN
export const APP_STATES = {
  INITIAL: 'initial' as const,
  HOME: 'home' as const,
  SOLUTIONS: 'solutions' as const
} as const;

// CONFIGURACIÓN DE AR.js
export const AR_CONFIG: ARConfig = {
  sourceType: 'webcam',
  debugUIEnabled: false,
  detectionMode: 'mono_and_matrix',
  matrixCodeType: '3x3',
  cameraParametersUrl: 'https://cdn.jsdelivr.net/gh/AR-js-org/AR.js@3.4.5/data/data/camera_para.dat',
  maxDetectionRate: 60,
  canvasWidth: 640,
  canvasHeight: 480
} as const;

// CONFIGURACIÓN DE CÁMARA
export const CAMERA_CONFIG: CameraConfig = {
  position: [0, 0, 0],
  cursor: null,
  raycaster: null
} as const;

// NOMBRES DE BOTONES PARA UI
export const BUTTON_NAMES: Record<string, string> = {
  facephiButton: 'Facephi',
  webButton: 'Web',
  linkedinButton: 'LinkedIn',
  locationButton: 'Ubicación',
  solutionButton: 'Solutions',
  auButton: 'Authentication',
  isButton: 'Identity', 
  onButton: 'Onboarding',
  goHomeButton: 'GoHome'
} as const;

// CONFIGURACIÓN DE ÁREAS ESTÁTICAS (FALLBACK)
export const STATIC_AREAS: Record<AppState, Record<string, ClickableArea>> = {
  home: {
    facephiButton: { 
      id: 'facephiButton',
      x1: 0.25, 
      y1: 0.08, 
      x2: 0.75, 
      y2: 0.22 
    },
    webButton: { 
      id: 'webButton',
      x1: 0.05, 
      y1: 0.30, 
      x2: 0.35, 
      y2: 0.50 
    },
    linkedinButton: { 
      id: 'linkedinButton',
      x1: 0.35, 
      y1: 0.30, 
      x2: 0.65, 
      y2: 0.50 
    },
    locationButton: { 
      id: 'locationButton',
      x1: 0.65, 
      y1: 0.30, 
      x2: 0.95, 
      y2: 0.50 
    },
    solutionButton: { 
      id: 'solutionButton',
      x1: 0.25, 
      y1: 0.55, 
      x2: 0.75, 
      y2: 0.75 
    }
  },
  solutions: {
    goHomeButton: { 
      id: 'goHomeButton',
      x1: 0.25, 
      y1: 0.08, 
      x2: 0.75, 
      y2: 0.22 
    },
    auButton: { 
      id: 'auButton',
      x1: 0.12, 
      y1: 0.36, 
      x2: 0.28, 
      y2: 0.54 
    },
    onButton: { 
      id: 'onButton',
      x1: 0.42, 
      y1: 0.36, 
      x2: 0.58, 
      y2: 0.54 
    },
    isButton: { 
      id: 'isButton',
      x1: 0.72, 
      y1: 0.36, 
      x2: 0.88, 
      y2: 0.54 
    }
  },
  initial: {}
} as const;

